import React from 'react';
import { useCustomEventListener } from 'react-custom-events';
import { useNavigate } from 'react-router-dom';

import mainPageConfig from '@client/config/mainPageConfig';
import { UxContext } from '@client/context/UxContext';
import useDateTimeFormatters from '@client/hooks/useDateTimeFormatters';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n, { getLocale, setLocale } from '@client/utils/i18n';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, IconButton, MenuItem, Popover, Toolbar, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

const DRAWER_WIDTH = 260;

const titleStyle = { flexGrow: 1 };
const flagStyle = { fontSize: '1.2rem' };

interface HeaderProps {
    children: React.ReactNode;
    colorMode: 'light' | 'dark';
    onThemeToggle: () => void;
}

function toTimeString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

const Header = ({ children, colorMode, onThemeToggle }: HeaderProps) => {
    const { handleSnackbar } = React.useContext(UxContext)!;
    const navigate = useNavigate();
    const theme = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [langAnchorEl, setLangAnchorEl] = React.useState<HTMLElement | null>(null);
    const locale = getLocale();
    const localeName = locale === 'en' ? 'English' : 'Magyar';
    const localeFlag = locale === 'en' ? '🇬🇧' : '🇭🇺';
    const { formatTime, formatDate } = useDateTimeFormatters();
    const {
        globalState: { userData, dateTimeFormatLoaded, serverTimeOffset },
        globalActions
    } = useGlobalStore();
    const [now, setNow] = React.useState(() => new Date(Date.now() + serverTimeOffset));
    const ref = React.useRef<HTMLDivElement>(null);

    const transitionOptions = {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    };

    let lastGroup: React.ReactNode;

    useCustomEventListener('lediumApiError', (data: string) => {
        handleSnackbar({
            message: data,
            severity: 'error'
        });
    });
    // const onSelectLanguage = (lang) => {
    //     window.lang = lang.toLocaleLowerCase();
    // }

    const onSelectPage = (page: string) => {
        navigate('/' + page);
        setIsDrawerOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
            setIsDrawerOpen(false);
        }
    };

    React.useEffect(() => {
        if (userData.status === 'LOGGED_OUT') {
            handleSnackbar({
                message: i18n('logout_successful') as string,
                severity: 'info'
            });
            navigate('/login');
        }
        if (userData.status === 'LOGGED_IN') {
            if (userData.auth === true) {
                handleSnackbar({
                    message: i18n('login_successful') as string,
                    severity: 'success'
                });
                navigate('/');
            } else {
                handleSnackbar({
                    message: i18n('login_failed') as string,
                    severity: 'error'
                });
            }
        }
    }, [userData.status, userData.auth, navigate, handleSnackbar]);

    function onClickLogout() {
        globalActions.users.callUserLogout();
    }

    React.useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    React.useEffect(() => {
        const timer = setInterval(() => setNow(new Date(Date.now() + serverTimeOffset)), 1000);
        return () => clearInterval(timer);
    }, [serverTimeOffset]);

    // for login screen
    if (!userData.auth) {
        return <>{children}</>;
    }

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(['width', 'margin'], transitionOptions),
                    ...(isDrawerOpen && {
                        marginLeft: DRAWER_WIDTH,
                        width: `calc(100% - ${DRAWER_WIDTH}px)`
                    })
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={() => setIsDrawerOpen(true)}
                        edge="start"
                        sx={{
                            marginRight: '36px',
                            ...(isDrawerOpen && { display: 'none' })
                        }}
                        size="large"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap style={titleStyle}>
                        {i18n('ledium_title')}
                    </Typography>
                    {dateTimeFormatLoaded && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <Typography variant="body2" noWrap sx={{ lineHeight: 1 }}>
                                {formatDate(now.getTime())}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ lineHeight: 1 }}>
                                {formatTime(toTimeString(now))}
                            </Typography>
                        </Box>
                    )}
                    <Button color="inherit" onClick={onClickLogout}>
                        {i18n('logout_user', userData.username)}
                    </Button>
                    {/* <ReactFlagsSelect*/}
                    {/*    countries={availableLocalisations.map(e => e.toLocaleUpperCase())}*/}
                    {/*    defaultCountry="GB"*/}
                    {/*    showSelectedLabel={false}*/}
                    {/*    showOptionLabel={false}*/}
                    {/*    selectedSize={18}*/}
                    {/*    optionsSize={18}*/}
                    {/*    onSelect={onSelectLanguage}*/}
                    {/* />*/}
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    width: isDrawerOpen ? DRAWER_WIDTH : '55px',
                    transition: theme.transitions.create('width', transitionOptions),
                    '& .MuiDrawer-paper': {
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create('width', transitionOptions),
                        width: isDrawerOpen ? DRAWER_WIDTH : '55px',
                        ...(isDrawerOpen ? { zIndex: 2000 } : { overflowX: 'hidden' })
                    }
                }}
                ref={ref}
            >
                <Box
                    sx={(t) => ({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: t.spacing(0, 1),
                        ...t.mixins.toolbar
                    })}
                >
                    <IconButton onClick={() => setIsDrawerOpen(false)} size="large">
                        <ChevronLeftIcon />
                    </IconButton>
                </Box>
                <Divider />
                <List sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                    {Object.values(mainPageConfig).map((page, idx) => {
                        let showGroup = null;
                        if (page.group && page.group !== lastGroup) {
                            lastGroup = page.group;
                            showGroup = page.group;
                        }

                        return (
                            <React.Fragment key={`menu-${idx}`}>
                                {showGroup ? (
                                    <>
                                        <Divider />
                                        <ListItem
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                mt: '5px',
                                                transition: theme.transitions.create(['height', 'opacity'], transitionOptions),
                                                ...(isDrawerOpen ? { height: '35px', opacity: 1 } : { height: 0, overflow: 'hidden', opacity: 0 })
                                            }}
                                        >
                                            {showGroup}
                                        </ListItem>
                                    </>
                                ) : null}
                                <Tooltip
                                    placement="right"
                                    arrow
                                    title={
                                        <>
                                            {isDrawerOpen ? null : <Typography variant="subtitle2">{page.title}</Typography>}
                                            <Typography variant="body2">{page.description}</Typography>
                                        </>
                                    }
                                >
                                    <ListItem button onClick={() => onSelectPage(page.page)}>
                                        <ListItemIcon>{page.icon}</ListItemIcon>
                                        <ListItemText primary={page.title} />
                                    </ListItem>
                                </Tooltip>
                            </React.Fragment>
                        );
                    })}
                </List>
                <Divider />
                <List disablePadding>
                    <Tooltip placement="right" title={isDrawerOpen ? '' : (i18n(colorMode === 'light' ? 'dark_mode' : 'light_mode') as string)}>
                        <ListItem button onClick={onThemeToggle}>
                            <ListItemIcon>{colorMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}</ListItemIcon>
                            <ListItemText primary={i18n(colorMode === 'dark' ? 'light_mode' : 'dark_mode') as string} />
                        </ListItem>
                    </Tooltip>
                    <Divider />
                    <Tooltip placement="right" title={isDrawerOpen ? '' : localeName}>
                        <ListItem button onClick={(e) => setLangAnchorEl(e.currentTarget)}>
                            <ListItemIcon sx={{ fontSize: '1.3rem' }}>{localeFlag}</ListItemIcon>
                            <ListItemText primary={localeName} />
                        </ListItem>
                    </Tooltip>
                </List>
                <Popover
                    open={Boolean(langAnchorEl)}
                    anchorEl={langAnchorEl}
                    onClose={() => setLangAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <MenuItem selected={locale === 'en'} onClick={() => setLocale('en')} sx={{ gap: 1.5 }}>
                        <span style={flagStyle}>🇬🇧</span>
                        <Typography variant="body2">English</Typography>
                    </MenuItem>
                    <MenuItem selected={locale === 'hu'} onClick={() => setLocale('hu')} sx={{ gap: 1.5 }}>
                        <span style={flagStyle}>🇭🇺</span>
                        <Typography variant="body2">Magyar</Typography>
                    </MenuItem>
                </Popover>
            </Drawer>
            <Box sx={{ ml: '55px' }}>
                <Toolbar />
                {children}
            </Box>
        </>
    );
};

export default Header;
